/**
 * Page de gestion des templates d'étiquettes
 * Permet de créer, modifier, dupliquer et supprimer des templates
 */

import { useState, useEffect } from 'react';
import { labelTemplatesApi } from '../api/label-templates.api';
import {
  LabelTemplate,
  LabelTemplateType,
  CreateLabelTemplateDto,
  UpdateLabelTemplateDto,
} from '../types/label-templates.types';
import { LabelTemplateEditor } from '../components/labels/LabelTemplateEditor';
import { PrintShelfLabelsModal } from '../components/labels/PrintShelfLabelsModal';
import { PriceChangesPanel } from '../components/labels/PriceChangesPanel';
import { FreetextLabelModal } from '../components/labels/FreetextLabelModal';

export default function LabelTemplates() {
  const [templates, setTemplates] = useState<LabelTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState<LabelTemplateType | 'ALL'>('ALL');
  const [showEditor, setShowEditor] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<LabelTemplate | null>(null);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [showFreetextModal, setShowFreetextModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'templates' | 'price-changes'>('templates');

  useEffect(() => {
    loadTemplates();
  }, [filterType]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const type = filterType === 'ALL' ? undefined : filterType;
      const data = await labelTemplatesApi.findAll(type);
      setTemplates(data);
    } catch (error) {
      console.error('Erreur chargement templates:', error);
      alert('Erreur lors du chargement des templates');
    } finally {
      setLoading(false);
    }
  };

  const handleSeedTemplates = async () => {
    if (!confirm('Voulez-vous créer les 3 templates par défaut ?')) return;

    try {
      await labelTemplatesApi.seed();
      alert('✅ 3 templates créés avec succès !');
      loadTemplates();
    } catch (error: any) {
      console.error('Erreur seed:', error);
      alert(error.response?.data?.message || 'Erreur lors du seed');
    }
  };

  const handleSetDefault = async (id: string) => {
    try {
      await labelTemplatesApi.setAsDefault(id);
      alert('Template défini comme par défaut');
      loadTemplates();
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la mise à jour');
    }
  };

  const handleDuplicate = async (template: LabelTemplate) => {
    const newName = prompt('Nom du nouveau template:', `${template.name} (copie)`);
    if (!newName) return;

    try {
      await labelTemplatesApi.duplicate(template.id, newName);
      alert('Template dupliqué avec succès');
      loadTemplates();
    } catch (error: any) {
      console.error('Erreur:', error);
      alert(error.response?.data?.message || 'Erreur lors de la duplication');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce template ?')) return;

    try {
      await labelTemplatesApi.delete(id);
      alert('Template supprimé');
      loadTemplates();
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const handleEdit = (template: LabelTemplate) => {
    setEditingTemplate(template);
    setShowEditor(true);
  };

  const handleCreate = () => {
    setEditingTemplate(null);
    setShowEditor(true);
  };

  const handleSaveTemplate = async (data: CreateLabelTemplateDto | UpdateLabelTemplateDto) => {
    try {
      if (editingTemplate) {
        await labelTemplatesApi.update(editingTemplate.id, data as UpdateLabelTemplateDto);
        alert('Template modifié avec succès');
      } else {
        await labelTemplatesApi.create(data as CreateLabelTemplateDto);
        alert('Template créé avec succès');
      }
      setShowEditor(false);
      setEditingTemplate(null);
      loadTemplates();
    } catch (error: any) {
      console.error('Erreur:', error);
      alert(error.response?.data?.message || 'Erreur lors de l\'enregistrement');
    }
  };

  const getTypeLabel = (type: LabelTemplateType) => {
    const labels = {
      SHELF: 'Rayonnage',
      MEDIUM: 'Medium',
      BULK: 'Vrac',
      PROMO: 'Promotion',
      PREORDER: 'Précommande',
      CUSTOM: 'Personnalisé',
    };
    return labels[type] || type;
  };

  const getTypeColor = (type: LabelTemplateType) => {
    const colors = {
      SHELF: 'bg-blue-100 text-blue-800',
      MEDIUM: 'bg-purple-100 text-purple-800',
      BULK: 'bg-green-100 text-green-800',
      PROMO: 'bg-red-100 text-red-800',
      PREORDER: 'bg-yellow-100 text-yellow-800',
      CUSTOM: 'bg-gray-100 text-gray-800',
    };
    return colors[type] || colors.CUSTOM;
  };

  if (showEditor) {
    return (
      <LabelTemplateEditor
        template={editingTemplate}
        onSave={handleSaveTemplate}
        onCancel={() => {
          setShowEditor(false);
          setEditingTemplate(null);
        }}
      />
    );
  }

  return (
    <div className="max-w-7xl mx-auto sm:px-6 lg:px-8 sm:py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-wrap justify-between items-center gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Étiquettes Zebra</h1>
            <p className="mt-1 text-sm text-gray-500">
              Templates, impression et suivi des prix
            </p>
          </div>
          <div className="flex flex-wrap gap-2 sm:gap-3">
            <button
              onClick={() => setShowFreetextModal(true)}
              className="px-4 py-2 border border-green-600 rounded-md shadow-sm text-sm font-medium text-green-600 bg-white hover:bg-green-50"
            >
              Texte libre
            </button>
            {activeTab === 'templates' && (
              <>
                <button
                  onClick={handleSeedTemplates}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Seed Templates
                </button>
                <button
                  onClick={() => setShowPrintModal(true)}
                  className="px-4 py-2 border border-indigo-600 rounded-md shadow-sm text-sm font-medium text-indigo-600 bg-white hover:bg-indigo-50"
                >
                  Imprimer
                </button>
                <button
                  onClick={handleCreate}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  + Nouveau Template
                </button>
              </>
            )}
          </div>
        </div>

        {/* Onglets */}
        <div className="mt-6 border-b border-gray-200">
          <nav className="-mb-px flex gap-6 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <button
              onClick={() => setActiveTab('templates')}
              className={`shrink-0 whitespace-nowrap py-3 px-1 border-b-2 text-sm font-medium ${
                activeTab === 'templates'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Templates
            </button>
            <button
              onClick={() => setActiveTab('price-changes')}
              className={`shrink-0 whitespace-nowrap py-3 px-1 border-b-2 text-sm font-medium ${
                activeTab === 'price-changes'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Prix à ré-étiqueter
            </button>
          </nav>
        </div>

        {/* Filtres (seulement pour l'onglet templates) */}
        {activeTab === 'templates' && (
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              onClick={() => setFilterType('ALL')}
              className={`px-3 py-1 rounded-md text-sm font-medium ${
                filterType === 'ALL'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              Tous
            </button>
            {Object.values(LabelTemplateType).map((type) => (
              <button
                key={type}
                onClick={() => setFilterType(type)}
                className={`px-3 py-1 rounded-md text-sm font-medium ${
                  filterType === type
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
              >
                {getTypeLabel(type)}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Contenu onglet Templates */}
      {activeTab === 'templates' && (
        <>
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              <p className="mt-2 text-sm text-gray-500">Chargement...</p>
            </div>
          ) : templates.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg border-2 border-dashed border-gray-300">
              <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun template</h3>
              <p className="mt-1 text-sm text-gray-500">
                Commencez par créer un template ou utiliser le seed.
              </p>
              <div className="mt-6">
                <button
                  onClick={handleCreate}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  + Nouveau Template
                </button>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {templates.map((template) => (
                <div
                  key={template.id}
                  className="bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="p-6">
                    {/* Header carte */}
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="text-lg font-medium text-gray-900">{template.name}</h3>
                          {template.is_default && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                              Défaut
                            </span>
                          )}
                        </div>
                        <span className={`mt-1 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeColor(template.type)}`}>
                          {getTypeLabel(template.type)}
                        </span>
                      </div>
                    </div>

                    {/* Description */}
                    {template.description && (
                      <p className="mt-2 text-sm text-gray-500">{template.description}</p>
                    )}

                    {/* Dimensions */}
                    <div className="mt-4 flex items-center gap-4 text-sm text-gray-600">
                      <div className="flex items-center gap-1">
                        <span className="font-medium">Dimensions:</span>
                        <span>{template.width_mm}×{template.height_mm} mm</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="font-medium">DPI:</span>
                        <span>{template.dpi}</span>
                      </div>
                    </div>

                    {/* Champs actifs */}
                    <div className="mt-3 text-sm text-gray-600">
                      <span className="font-medium">Champs:</span>
                      <span className="ml-1">
                        {template.fields_config.filter(f => f.enabled).length} actifs
                      </span>
                    </div>

                    {/* Actions */}
                    <div className="mt-6 flex flex-wrap gap-2">
                      <button
                        onClick={() => handleEdit(template)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                      >
                        ✏️ Modifier
                      </button>
                      <button
                        onClick={() => handleDuplicate(template)}
                        className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                      >
                        📋
                      </button>
                      {!template.is_default && (
                        <button
                          onClick={() => handleSetDefault(template.id)}
                          className="px-3 py-2 border border-yellow-300 rounded-md text-sm font-medium text-yellow-700 bg-yellow-50 hover:bg-yellow-100"
                        >
                          ⭐
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(template.id)}
                        className="px-3 py-2 border border-red-300 rounded-md text-sm font-medium text-red-700 bg-red-50 hover:bg-red-100"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Contenu onglet Prix à ré-étiqueter */}
      {activeTab === 'price-changes' && <PriceChangesPanel />}

      {/* Modal d'impression */}
      {showPrintModal && (
        <PrintShelfLabelsModal onClose={() => setShowPrintModal(false)} />
      )}

      {/* Modal texte libre */}
      {showFreetextModal && (
        <FreetextLabelModal onClose={() => setShowFreetextModal(false)} />
      )}
    </div>
  );
}
